import React from "react";
import { 
  makeStyles, 
  withStyles, 
  Input, 
  InputAdornment, 
  Button, 
  Checkbox, 
  FormControlLabel,
  Typography,
  Box,
  CircularProgress,
  Paper,
  IconButton,
  Snackbar
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginSuccess, loginFailure } from "../Redux/auth/action";
import axios from "axios";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: "#f5f5f5",
    padding: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(4),
    width: "100%",
    maxWidth: "450px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  title: {
    textAlign: "center",
    marginBottom: theme.spacing(3),
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  input: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  submitButton: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    fontWeight: "bold",
  },
  link: {
    textAlign: "center",
    marginTop: theme.spacing(2),
    color: theme.palette.primary.main,
    cursor: "pointer",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  checkboxContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing(1),
  },
  errorMessage: {
    backgroundColor: "#ffebee",
    color: "#c62828",
    padding: theme.spacing(1.5),
    borderRadius: "4px",
    marginBottom: theme.spacing(2),
    border: "1px solid #ef5350",
  },
  successMessage: {
    backgroundColor: "#e8f5e8",
    color: "#2e7d32",
    padding: theme.spacing(1.5),
    borderRadius: "4px",
    marginBottom: theme.spacing(2),
    border: "1px solid #4caf50",
  },
}));

const BlackCheckbox = withStyles({
  root: {
    "&$checked": {
      color: "black",
    },
    width: "14px",
    height: "14px",
  },
  checked: {},
})((props) => <Checkbox size="small" color="default" {...props} />);

function LoginPage() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAuth = useSelector(state => state.auth.isAuth);
  const user = useSelector(state => state.auth.user);

  const [values, setValues] = React.useState({
    email: "",
    password: "",
    showPassword: false,
    rememberMe: false,
  });

  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [showError, setShowError] = React.useState(false);

  const handleChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.value });
    setError("");
    setShowError(false);
  };

  const handleCheckboxChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.checked });
  };

  const handleClickShowPassword = () => {
    setValues({ ...values, showPassword: !values.showPassword });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleCloseError = () => {
    setShowError(false);
  };

  React.useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    const savedRememberMe = localStorage.getItem('rememberMe') === 'true';
    
    if (savedRememberMe && savedEmail) {
      setValues(prev => ({
        ...prev,
        email: savedEmail,
        rememberMe: true
      }));
    }
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!values.email || !values.password) {
      setError("Email and password are required");
      setShowError(true);
      return;
    }

    setIsLoading(true);
    setError("");
    setShowError(false);

    try {
      const response = await axios.post("https://jtimes.zuddo.biz/auth/login", {
        email: values.email,
        password: values.password
      });
      if (response.status === 200) { 
        const data = response.data;
        
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        localStorage.setItem('user', JSON.stringify(data.user));

        if (values.rememberMe) {
          localStorage.setItem('rememberedEmail', values.email);
          localStorage.setItem('rememberMe', 'true');
        } else {
          localStorage.removeItem('rememberedEmail');
          localStorage.removeItem('rememberMe');
        }
        dispatch(loginSuccess(data.user));
        
        if (data.user.is_admin) {
          navigate("/admin/dashboard");
        } else {
          navigate("/");
        }
      }
    } catch (error) {
    
      let errorMessage = "Login failed. Please try again.";
      
      if (error.response) {
        errorMessage = error.response.data.error || errorMessage;
        
        if (error.response.status === 401) {
          errorMessage = "Invalid email or password";
        } else if (error.response.status === 403) {
          errorMessage = "Account deactivated. Please contact support.";
        } else if (error.response.status === 404) {
          errorMessage = "User not found. Please check your email.";
        }
      } else if (error.request) {
        errorMessage = "Network error. Please check your connection.";
      }
      
      setError(errorMessage);
      setShowError(true);
      dispatch(loginFailure());
    } finally {
      setIsLoading(false);
    }
  };

  if (isAuth && user) {
    if (user.is_admin) {
      return <Navigate to="/admin/dashboard" />;
    } else {
      return <Navigate to="/" />;
    }
  }

  return (
    <div className={classes.root}>
      <Paper className={classes.paper} elevation={3}>
        <Typography variant="h4" component="h1" className={classes.title}>
          Sign In
        </Typography>

        {showError && (
          <div className={classes.errorMessage}>
            <Typography variant="body2">
              {error}
            </Typography>
          </div>
        )}

        <form onSubmit={handleSubmit} className={classes.form}>
          <Typography variant="body1" gutterBottom>
            <b>Email Address</b>
          </Typography>
          <Input
            className={classes.input}
            type="email"
            value={values.email}
            onChange={handleChange("email")}
            required
            fullWidth
            placeholder="Enter your email"
            disabled={isLoading}
          />

          <Typography variant="body1" gutterBottom>
            <b>Password</b>
          </Typography>
          <Input
            className={classes.input}
            type={values.showPassword ? "text" : "password"}
            value={values.password}
            onChange={handleChange("password")}
            required
            fullWidth
            placeholder="Enter your password"
            disabled={isLoading}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  disabled={isLoading}
                >
                  {values.showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />

          <Box className={classes.checkboxContainer}>
            <FormControlLabel
              control={
                <BlackCheckbox
                  checked={values.rememberMe}
                  onChange={handleCheckboxChange("rememberMe")}
                  disabled={isLoading}
                />
              }
              label={<Typography variant="body2">Remember me</Typography>}
            />
            <Link 
              to="/forgot-password" 
              className={classes.link}
              style={{ fontSize: "14px" }}
            >
              Forgot your password?
            </Link>
          </Box>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            className={classes.submitButton}
            disabled={isLoading}
            fullWidth
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Sign In'
            )}
          </Button>
        </form>

        <Typography variant="body2" style={{ textAlign: "center", marginTop: "20px" }}>
          Don't have an account?{" "}
          <Link to="/registeration" className={classes.link}>
            <b>Create one</b>
          </Link>
        </Typography>

        <Snackbar
          open={showError}
          autoHideDuration={6000}
          onClose={handleCloseError}
          message={error}
        />
      </Paper>
    </div>
  );
}

export { LoginPage };